import { APOLLO_OPTIONS } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { Injector, NgModule, inject } from '@angular/core';
import {
  ApolloClientOptions,
  ApolloLink,
  CombinedGraphQLErrors,
  InMemoryCache,
} from '@apollo/client/core';
import { ErrorLink } from '@apollo/client/link/error';
import { HttpHeaders } from '@angular/common/http';
import { catchError, switchMap, take, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { TokenRefreshCoordinatorService } from '../shared/services/auth/token-refresh-coordinator.service';

const uri = `${environment.apiBaseGraphql}graphql`;

/** Marca en el contexto de la operación que ya se reintentó tras refrescar el token. */
const AUTH_RETRY_CONTEXT_KEY = '__goliiiveAuthRetried';

/**
 * ¿Este error de GraphQL significa "token vencido/inválido"?
 *
 * A diferencia de REST, el backend de GraphQL NO responde 401: un
 * `UnauthorizedException` lanzado dentro de un resolver/guard viaja como error
 * de GraphQL (HTTP 200 + `errors[]`). Por eso el `tokenInterceptor`, que solo
 * mira `error.status === 401`, nunca se enteraba y jamás disparaba el refresh
 * para GraphQL: la query moría en silencio y el componente se quedaba con la
 * lista vacía (selects en "No items found" sin aviso ni logout).
 *
 * Se aceptan varias formas porque el código que expone Nest+Apollo cambia según
 * versión y según si la excepción atraviesa el filtro global.
 */
export function isGraphqlAuthError(error: unknown): boolean {
  if (!CombinedGraphQLErrors.is(error)) {
    return false;
  }
  return error.errors.some((gqlError) => {
    const ext = (gqlError.extensions ?? {}) as Record<string, any>;
    const status =
      ext['statusCode'] ??
      ext['status'] ??
      ext['originalError']?.statusCode ??
      ext['originalError']?.status ??
      ext['exception']?.status;
    if (Number(status) === 401) {
      return true;
    }
    if (ext['code'] === 'UNAUTHENTICATED') {
      return true;
    }
    return /unauthorized/i.test(String(gqlError.message ?? ''));
  });
}

export function createApollo(httpLink: HttpLink, injector: Injector): ApolloClientOptions {
  const authMiddleware = new ApolloLink((operation: any, forward: any) => {

    const token = JSON.parse(localStorage.getItem('sessionAuthState') || '{}').access_token || '';

    operation.setContext({
      headers: new HttpHeaders().set(
        "Authorization",
        `Bearer ${token}` || '',
      ),
    });

    return forward(operation);
  });

  /**
   * Refresh reactivo para GraphQL, equivalente al `handle401` del `tokenInterceptor`.
   *
   * Va ANTES de `authMiddleware` en la cadena a propósito: al reintentar con
   * `forward(operation)` se vuelve a ejecutar el middleware, que relee el
   * `access_token` de localStorage y adjunta el recién rotado.
   *
   * Usa el MISMO `TokenRefreshCoordinatorService` que el interceptor, así que un
   * 401 de REST y un error de auth de GraphQL en paralelo comparten una sola
   * llamada a `/refresh-token` (sin doble rotación ni REPLAY_DETECTED).
   *
   * Solo actúa sobre errores de nivel GraphQL. Un 401 HTTP real ya lo resuelve el
   * interceptor (refresca y reintenta la request), y Apollo recibe la respuesta
   * buena de forma transparente: reintentar acá también sería refrescar dos veces.
   *
   * El coordinator se resuelve de forma diferida vía `Injector` para no crear un
   * ciclo de DI al construir Apollo (mismo criterio que el interceptor con
   * `ModalActionService`).
   */
  const authRetryLink = new ErrorLink(({ error, operation, forward }) => {
    if (!isGraphqlAuthError(error)) {
      return;
    }
    // Una sola vuelta: si tras refrescar sigue fallando, es un fallo real.
    if (operation.getContext()[AUTH_RETRY_CONTEXT_KEY]) {
      return;
    }
    operation.setContext({ [AUTH_RETRY_CONTEXT_KEY]: true });

    const coordinator = injector.get(TokenRefreshCoordinatorService);
    return coordinator.refresh().pipe(
      take(1),
      switchMap(() => forward(operation)),
      // Si el refresh falla (o el reintento vuelve a romper) propagamos el error
      // ORIGINAL. El logout lo maneja el interceptor cuando el backend rechaza el
      // refresh_token: mantenemos esa responsabilidad en un solo lugar.
      catchError(() => throwError(() => error)),
    );
  });

  const http = httpLink.create({ uri });

  return {
    link: ApolloLink.from([
      authRetryLink,
      authMiddleware,
      http as unknown as ApolloLink,
    ]),
    cache: new InMemoryCache(),
    defaultOptions: {
      query: {
        fetchPolicy: 'network-only',
      },
    },
  };
}

@NgModule({
  providers: [
    {
      provide: APOLLO_OPTIONS,
      useFactory: () => createApollo(inject(HttpLink), inject(Injector)),
    },
  ],
})
export class GraphQLModule { }
